import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Copy, Share2, QrCode } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QRScanModal from "../components/QRScanModal";
import SpotCard from "../components/SpotCard";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState, ErrorState, CardSkeleton } from "../components/States";
import { api } from "../lib/api";
import { getRegion, regionCenter } from "../lib/region";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime, fmtDateTimeZone, fmtMoney, setPageTitle } from "../lib/format";
import { fadeUp, revealOnce, staggerParent } from "../lib/motion";

export default function Dashboard() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [nearby, setNearby] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [mine, setMine] = useState(null);
  const [error, setError] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [propCode, setPropCode] = useState("");
  const navigate = useNavigate();

  const copyCode = () => {
    if (!rewards?.referral_code) return;
    navigator.clipboard?.writeText(rewards.referral_code).then(() => toast.success("Referral code copied"));
  };
  const shareLink = async () => {
    if (!rewards) return;
    const payload = { title: "MyRedPark", text: rewards.share_message, url: rewards.share_url || undefined };
    if (navigator.share) { try { await navigator.share(payload); } catch { /* dismissed */ } }
    else {
      navigator.clipboard?.writeText(`${rewards.share_message} ${rewards.share_url || ""}`.trim())
        .then(() => toast.success("Referral link copied"));
    }
  };

  const load = () => {
    setError(null);
    api.driverDashboard().then(setDash).catch((e) => setError(e.message));
    getRegion().then((rg) => { const c = regionCenter(rg?.country); return api.nearby(c.lat, c.lng, 25); })
      .then((r) => setNearby(r.slice(0, 4))).catch(() => setNearby([]));
    api.rewardsMe().then(setRewards).catch(() => {});
    api.myBookings().then(setMine).catch(() => setMine([]));
  };
  useEffect(() => { setPageTitle("Dashboard"); load(); }, []);

  const next = dash?.recent?.find((b) => ["pending", "pending_auth", "authorized", "paid"].includes(b.status) && new Date(b.start_time) > new Date());

  // CURRENT PARKING (Part 12): the driver's live/confirmed booking, surfaced
  // globally from the existing bookings API — ongoing session first, then the
  // next confirmed one. No data is duplicated; it disappears when it ends.
  const nowTs = Date.now();
  const current = mine?.find((b) => ["authorized", "paid"].includes(b.status)
      && new Date(b.start_time).getTime() <= nowTs && new Date(b.end_time).getTime() > nowTs)
    || mine?.find((b) => ["authorized", "paid"].includes(b.status) && new Date(b.start_time).getTime() > nowTs);

  return (
    <div className="min-h-screen flex flex-col" data-testid="driver-dashboard">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <motion.div variants={staggerParent(0.06)} initial="hidden" animate="show" className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <motion.p variants={fadeUp} className="section-label">Driver</motion.p>
            <motion.h1 variants={fadeUp} className="type-display mt-3">Hey, {user?.name?.split(" ")[0] || "driver"}.</motion.h1>
          </div>
          <motion.div variants={fadeUp} className="flex gap-2.5">
            <button onClick={() => setScanOpen(true)} data-testid="dashboard-scan-qr" className="btn-secondary text-sm"><QrCode className="w-4 h-4" /> Scan QR</button>
            <Link to="/find" data-testid="dashboard-quick-search" className="btn-primary text-sm"><Search className="w-4 h-4" /> Find Parking</Link>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-3 flex items-center gap-2.5" data-testid="dashboard-property-code">
            <input value={propCode} onChange={(e) => setPropCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                   onKeyDown={(e) => { if (e.key === "Enter" && /^\d{4}$/.test(propCode)) navigate(`/p/${propCode}`); }}
                   inputMode="numeric" maxLength={4} placeholder="4821" aria-label="Enter property code"
                   data-testid="dashboard-property-code-input"
                   className="w-28 rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white font-mono tracking-[0.25em] text-center focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors" />
            <button onClick={() => { if (/^\d{4}$/.test(propCode)) navigate(`/p/${propCode}`); else toast.error("Enter the 4-digit property code."); }}
                    data-testid="dashboard-property-code-go" className="btn-primary text-sm tracking-widest">ENTER</button>
          </motion.div>
        </motion.div>

        {error ? <div className="mt-10"><ErrorState body={error} onRetry={load} /></div> : !dash ? <div className="mt-10"><PageLoading label="Loading your dashboard…" /></div> : (
          <>
            <motion.div variants={staggerParent(0.07)} {...revealOnce} className="mt-10 grid grid-cols-2 lg:grid-cols-4" data-testid="driver-stats">
              {[
                { label: "Upcoming", value: dash.upcoming_bookings, tid: "stat-upcoming", to: "/bookings?tab=upcoming" },
                { label: "Ongoing", value: dash.ongoing_bookings, tid: "stat-ongoing", to: "/bookings?tab=active" },
                { label: "Completed", value: dash.completed_bookings, tid: "stat-completed", to: "/bookings?tab=completed" },
                { label: "Cancelled", value: dash.cancelled_bookings, tid: "stat-cancelled", to: "/bookings?tab=cancelled" },
              ].map((s, i) => (
                <motion.div key={s.label} variants={fadeUp} className={`${i > 0 ? "sm:pl-6 sm:border-l border-[#1F2328]" : ""} ${i >= 2 ? "border-t sm:border-t-0 border-[#1F2328] max-sm:pt-6" : ""}`}>
                  <Link to={s.to} data-testid={s.tid}
                        className={`block py-6 pr-6 rounded-lg group transition-colors hover:bg-white/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E50914]`}>
                    <p className="font-display font-bold text-4xl tracking-tight group-hover:text-[#FF3B45] transition-colors">{s.value}</p>
                    <p className="meta-label mt-1.5">{s.label}</p>
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            <div className="mt-14 grid lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="type-h2">Recent activity</h2>
                  <Link to="/bookings" className="btn-ghost-red text-xs" data-testid="view-all-bookings">All bookings <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
                {dash.recent?.length ? (
                  <div className="divide-y divide-[#18181B] border-y border-[#18181B]" data-testid="recent-bookings">
                    {dash.recent.map((b) => (
                      <Link key={b.booking_id} to={`/bookings/${b.booking_id}`} className="group flex items-center justify-between gap-4 py-4 transition-colors hover:bg-white/[0.02] -mx-3 px-3 rounded-lg" data-testid={`recent-booking-${b.booking_id}`}>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate group-hover:text-white transition-colors">{b.spot_title}</p>
                          <p className="text-xs text-zinc-600 mt-1">{fmtDateTime(b.start_time)}</p>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <span className="font-display font-semibold text-sm">{fmtMoney(b.total)}</span>
                          <StatusBadge value={b.status} />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-[#27272A] rounded-2xl py-14 text-center" data-testid="no-recent-bookings">
                    <p className="text-sm text-zinc-400">No bookings yet. <Link to="/find" className="text-[#FF3B45] hover:underline">Find your first spot</Link>.</p>
                  </div>
                )}
              </div>
              <div className="space-y-6">
                {current && (
                  <div className="card-dark p-6 border-[#E50914]/40 shadow-[0_0_32px_rgba(229,9,20,0.12)]" data-testid="current-parking-card">
                    <div className="flex items-center justify-between gap-3">
                      <p className="meta-label !text-[#FF3B45]">Current parking</p>
                      <StatusBadge value={current.status} />
                    </div>
                    <p className="mt-3 font-display font-semibold text-lg tracking-tight">{current.spot_title}</p>
                    <p className="text-xs text-zinc-500 mt-1.5" data-testid="current-parking-times">
                      {fmtDateTimeZone(current.start_time, current.timezone)} <ArrowRight className="inline w-3 h-3 text-zinc-600 -mt-0.5" aria-hidden="true" /> {fmtDateTimeZone(current.end_time, current.timezone)}
                    </p>
                    <Link to={`/bookings/${current.booking_id}`} data-testid="current-parking-view" className="btn-primary w-full mt-4 !h-10 text-xs">
                      View Booking <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                )}
                <div>
                  <h2 className="type-h2 mb-5">Next up</h2>
                  {next ? (
                    <Link to={`/bookings/${next.booking_id}`} className="card-dark card-dark-hover p-6 block" data-testid="upcoming-booking-card">
                      <StatusBadge value={next.status} />
                      <p className="mt-4 font-display font-semibold text-lg tracking-tight">{next.spot_title}</p>
                      <p className="text-xs text-zinc-500 mt-1.5">{next.spot_address}</p>
                      <p className="text-xs text-zinc-600 mt-3">{fmtDateTime(next.start_time)}</p>
                      <p className="mt-4 font-display font-bold text-xl">{fmtMoney(next.total)}</p>
                    </Link>
                  ) : (
                    <div className="border border-dashed border-[#27272A] rounded-2xl p-8 text-sm text-zinc-500" data-testid="no-upcoming-booking">Nothing scheduled. Book a spot and it'll show up here.</div>
                  )}
                </div>
                <div className="card-dark p-6" data-testid="spending-card">
                  <p className="meta-label">Spent this month</p>
                  <p className="font-display font-bold text-3xl tracking-tight mt-2">{fmtMoney(dash.spent_month)}</p>
                  <Link to="/wallet" className="btn-ghost-red text-xs mt-4 inline-flex">Open wallet <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
                {rewards && (
                  <div className="card-dark p-6" data-testid="rewards-referrals-card">
                    <p className="meta-label">Rewards & Referrals</p>
                    {rewards.best_offer ? (
                      <p className="mt-3 text-sm" data-testid="rewards-best-offer">
                        <span className="font-display font-bold text-2xl text-[#FF3B45]">{rewards.best_offer.discount_percent}% OFF</span>{" "}
                        <span className="text-zinc-500 text-xs">your first booking</span>
                      </p>
                    ) : rewards.spin_reward?.status === "redeemed" ? (
                      <p className="mt-3 text-xs text-emerald-400" data-testid="rewards-redeemed-note">First-booking reward redeemed</p>
                    ) : null}
                    {(rewards.offer_eligibility || []).map((o) => (
                      <p key={o.offer_type} className="mt-2 text-[11px] text-zinc-500" data-testid={`offer-cooldown-${o.offer_type}`}>{o.message}</p>
                    ))}
                    <div className="mt-4 border border-dashed border-[#27272A] rounded-xl p-3.5">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-600">Your referral code</p>
                      <div className="flex items-center justify-between mt-1.5 gap-2">
                        <span className="font-mono font-bold tracking-widest text-sm" data-testid="referral-code">{rewards.referral_code}</span>
                        <button data-testid="copy-referral-code" onClick={copyCode} className="btn-ghost-red text-[11px]"><Copy className="w-3 h-3" /> Copy</button>
                      </div>
                      <button data-testid="share-referral-link" onClick={shareLink} className="btn-primary w-full mt-3 !h-9 text-xs">
                        <Share2 className="w-3.5 h-3.5" /> Share — friends get 10% off
                      </button>
                      <p className="text-[11px] text-zinc-600 mt-2.5" data-testid="referral-stats">
                        {rewards.referral_stats.attributed} joined · {rewards.referral_stats.redeemed} booked
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <div className="mt-16">
          <div className="flex items-end justify-between mb-5">
            <h2 className="type-h2">Near you</h2>
            <Link to="/find" className="btn-ghost-red text-xs" data-testid="browse-all-spots">Browse all <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          {nearby === null ? <CardSkeleton count={4} /> : nearby.length === 0 ? (
            <EmptyState title="No listings near you yet" body="New hosts join every day — check back soon." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="nearby-spots">
              {nearby.map((s) => <SpotCard key={s.spot_id} spot={s} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <QRScanModal open={scanOpen} onClose={() => setScanOpen(false)} />
    </div>
  );
}
